import React from "react";
import { theme } from "../../../Themes/Themes";
import "./JSONViewer.css";

interface JSONViewerProps {
  jsonString: string;
  title?: string;
  maxHeight?: string;
}

export const JSONViewer: React.FC<JSONViewerProps> = ({
  jsonString,
  title,
  maxHeight = "300px",
}) => {
  const parseAndFormatJSON = (jsonStr: string): string | null => {
    try {
      const parsed = JSON.parse(jsonStr);
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      return null;
    }
  };

  const formatJSONWithSyntaxHighlighting = (jsonStr: string): JSX.Element => {
    const formatted = parseAndFormatJSON(jsonStr);
    if (!formatted) {
      return <span style={{ color: "#d32f2f" }}>Invalid JSON</span>;
    }

    const lines = formatted.split("\n");
    return (
      <>
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          let coloredLine = line;

          // Color property names (strings before colons)
          coloredLine = coloredLine.replace(
            /"([^"]+)":/g,
            `<span class="json-property">"$1":</span>`
          );

          // Color string values
          coloredLine = coloredLine.replace(
            /:\s*"([^"]*)"([,}]?)/g,
            `: <span class="json-string">"$1"</span>$2`
          );

          // Color numbers
          coloredLine = coloredLine.replace(
            /:\s*(\d+(?:\.\d+)?)([,}]?)/g,
            `: <span class="json-number">$1</span>$2`
          );

          // Color booleans
          coloredLine = coloredLine.replace(
            /:\s*(true|false)([,}]?)/g,
            `: <span class="json-boolean">$1</span>$2`
          );

          // Color null
          coloredLine = coloredLine.replace(
            /:\s*(null)([,}]?)/g,
            `: <span class="json-null">$1</span>$2`
          );

          // Color brackets and braces
          coloredLine = coloredLine.replace(
            /([{}[\]])/g,
            '<span class="json-bracket">$1</span>'
          );

          return (
            <div
              key={index}
              className="json-line"
              dangerouslySetInnerHTML={{ __html: coloredLine }}
            />
          );
        })}
      </>
    );
  };

  return (
    <div className="json-viewer-container">
      {title && (
        <div className="json-viewer-title">
          <strong>{title}</strong>
        </div>
      )}
      <div
        className="json-viewer-content"
        style={{
          maxHeight,
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
          borderRadius: "6px",
          padding: "12px",
          fontFamily: "Monaco, Menlo, 'Ubuntu Mono', monospace",
          fontSize: "13px",
          lineHeight: "1.4",
          overflow: "auto",
          position: "relative",
          whiteSpace: "pre",
        }}
      >
        {formatJSONWithSyntaxHighlighting(jsonString)}
      </div>
    </div>
  );
};

export default JSONViewer;
